/**
 * O visor de desenho — o SVG do motor posto na tela com segurança.
 *
 * Nasceu dentro da tela do pinout gráfico e saiu de lá quando o pinout interno
 * precisou do mesmo: **os dois desenhos vêm do mesmo motor**, com a mesma
 * higienização, o mesmo dimensionamento e o mesmo cronômetro. Duplicar isso
 * seria duplicar também a parte que protege a página.
 *
 * O que ele faz, e por quê:
 *
 * - **Higieniza antes de embutir** (`svgSeguro.ts`). O motor concatena texto do
 *   banco sem escapar, e SVG dentro do DOM é território de script.
 * - **Desenha no tamanho que o SVG pede**, não na largura da janela: o motor
 *   dimensiona pelo número de pinos, e esticar engorda tudo sem mostrar mais.
 * - **Aceita clique opcional** por classe. O desenho externo marca componentes
 *   (`SvgComponente`, com o `pinout_id` no `name`); o interno não marca nada, e
 *   aí o visor é só visor.
 * - **XML quebrado vira mensagem**, não quadro vazio — costuma ser um `&` sem
 *   escapar numa descrição.
 */

import { useEffect, useRef, useState } from "react"
import { higienizarSvg } from "./svgSeguro"

/** Os marcadores que o motor externo usa. O interno não tem nenhum. */
export const SELETOR_CLICAVEL =
  ".SvgComponente, .SvgComponenteIntermediario, .SvgComponentePrincipal"

export function DesenhoSvg({
  svg, largura, zoom, rotulo, aoClicar,
}: {
  svg: string
  /** Largura declarada pelo próprio SVG. 0 = desconhecida, aí ocupa o quadro. */
  largura: number
  zoom: number
  rotulo: string
  /**
   * Quando presente, os marcadores viram alvo de clique e recebem o elemento
   * que a pessoa acertou. Ausente: desenho só de olhar.
   */
  aoClicar?: (marcador: SVGElement) => void
}) {
  const caixa = useRef<HTMLDivElement>(null)
  const [erro, setErro] = useState<string | null>(null)
  const clicavel = aoClicar != null

  useEffect(() => {
    const onde = caixa.current
    if (!onde) return

    let elemento: SVGSVGElement
    try {
      elemento = higienizarSvg(svg).elemento
      setErro(null)
    } catch (e) {
      setErro(e instanceof Error ? e.message : "O desenho não pôde ser exibido.")
      onde.replaceChildren()
      return
    }

    // O tamanho passa a vir do CSS de fora: `width: 100%` do quadro, altura pela
    // proporção do viewBox. Sem o `height: auto` explícito, um SVG sem altura
    // dentro de um pai de altura automática pode fechar em zero.
    elemento.removeAttribute("width")
    elemento.removeAttribute("height")
    elemento.style.display = "block"
    elemento.style.width = "100%"
    elemento.style.height = "auto"
    elemento.setAttribute("role", "img")
    elemento.setAttribute("aria-label", rotulo)

    if (clicavel) {
      for (const g of Array.from(elemento.querySelectorAll<SVGElement>(SELETOR_CLICAVEL))) {
        g.style.cursor = "pointer"
        // Sem isto só o traço responde, e acertar uma linha de meio pixel com o
        // mouse é sofrimento.
        g.setAttribute("pointer-events", "all")
      }
    }

    onde.replaceChildren(elemento)
  }, [svg, rotulo, clicavel])

  if (erro) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        fontSize: 13, color: "var(--erro)", textAlign: "center", maxWidth: "52ch",
      }}>
        <b>Não deu para exibir o desenho.</b>
        <span style={{ fontSize: 12, color: "var(--txt2)", lineHeight: 1.5 }}>{erro}</span>
      </div>
    )
  }

  const px = largura > 0 ? largura * zoom : undefined

  return (
    <div
      ref={caixa}
      // Um ouvinte só, na caixa: o SVG tem centenas de elementos e pendurar um
      // por marcador custa memória à toa. O clique sobe até aqui e a gente
      // pergunta em quem ele começou.
      onClick={aoClicar
        ? (ev) => {
            const alvo = (ev.target as Element).closest<SVGElement>(SELETOR_CLICAVEL)
            if (alvo) aoClicar(alvo)
          }
        : undefined}
      style={{
        width: px ? `${px}px` : "100%", flex: "0 0 auto",
        background: "#FFFFFF", borderRadius: 8, boxShadow: "var(--sombra)", overflow: "hidden",
      }}
    />
  )
}

/**
 * O cronômetro do desenho.
 *
 * Gerar é lento — 18 s no ECM do 976, com 168 pinouts — e silêncio parece
 * travamento. Passando de 8 segundos ele explica por que está demorando, com o
 * número medido, em vez de um "aguarde" que não informa nada.
 */
export function Trabalhando({ segundos }: { segundos: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <div
        aria-hidden="true"
        style={{
          width: 28, height: 28, borderRadius: "50%",
          border: "2.5px solid var(--line2)", borderTopColor: "var(--brand)",
          animation: "girar .9s linear infinite",
        }}
      />
      <span className="num" style={{ fontSize: 20, fontWeight: 600 }}>{segundos.toFixed(1)} s</span>
      <span role="status" style={{ fontSize: 12.5, color: "var(--txt2)" }}>
        Montando o desenho a partir dos pinouts.
      </span>
      {segundos > 8 && (
        <span style={{ fontSize: 11.5, color: "var(--txt3)", maxWidth: "44ch", textAlign: "center", lineHeight: 1.5 }}>
          Componente com muitos pinos leva mais tempo — o ECM do projeto 976, com 168 pinouts, leva
          cerca de 18 segundos. Não está travado.
        </span>
      )}
      <style>{"@keyframes girar { to { transform: rotate(360deg) } }"}</style>
    </div>
  )
}

/** Botãozinho de baixar, o mesmo nas duas telas. */
export function Baixar({ rotulo, aoBaixar }: { rotulo: string; aoBaixar: () => void }) {
  return (
    <button
      onClick={aoBaixar}
      title={`Baixar em ${rotulo}`}
      style={{
        height: 22, padding: "0 9px", borderRadius: 5, border: "1px solid var(--line2)",
        background: "var(--surface)", fontSize: 11, fontWeight: 600, color: "var(--txt2)",
      }}
    >
      {rotulo}
    </button>
  )
}
